import React, { useCallback, useState, useEffect } from 'react';
import { Flex, Grid } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';

import useLocalStorage from '@Shared/utils/useLocalStorage';
import { SigninSteps } from '@Features/auth/signin';
import CompleteStep from '@Features/auth/signin/ui/CompleteStep';
import { Logo, LogoWhite, Bg } from '@Shared/assets/images';
import { LanguageDropdown } from '@Widgets/index';

import { STEP_PATHS, StepType, signupBase } from './stepPaths';

const { useBreakpoint } = Grid;

import './styles.less';

const styles: Record<string, React.CSSProperties> = {
    flex: {
        minWidth: '100vw',
        minHeight: '100vh',
        backgroundColor: '#F6F9FD',
        flexDirection: 'column',
        backgroundSize: 'cover',
    },
    main: {
        boxSizing: 'border-box',
        background: '#fff',
        height: 'fit-content',
        borderRadius: 'var(--ant-border-radius-lg)',
        position: 'relative',
    },
    mainWrapper: {
        width: '100%',
        justifyContent: 'center',
        overflow: 'auto',
        flex: 1,
        alignItems: 'center',
        padding: 20,
        position: 'relative',
    },
    buttons: {
        position: 'fixed',
        left: '10px',
        bottom: '10px',
    },
    topLogo: {
        width: 162,
        marginBottom: 24,
    },
};

const stepsKeys: StepType[] = ['AuthStep', 'CompanyStep', 'TrialStep'];

export default function Signin() {
    const breakpoints = useBreakpoint();
    const storage = useLocalStorage();
    const [currentStep, setCurrentStep] = useState<StepType>(storage.read('currentStep', stepsKeys[0]) as StepType);
    const location = useLocation();
    const navigate = useNavigate();
    const isComplete = location.pathname.startsWith('/complete');

    // The step shown is the one the visitor has reached, kept in the browser by the steps themselves; the address
    // follows it. An address ahead (/trial/ typed or bookmarked before step 2) or behind (the browser's back after
    // step 1, which the server has already taken) is replaced by the step's own. Replaced, not pushed: the browser's
    // back leaves the sign-up, as it did when every step had one address. The query (?pid, utm_*, gclid, email) goes
    // along to every step.
    useEffect(() => {
        if (isComplete) return;
        const stepPath = signupBase(location.pathname) + STEP_PATHS[currentStep];
        if (location.pathname !== stepPath) {
            navigate({ pathname: stepPath, search: location.search }, { replace: true });
        }
    }, [currentStep, isComplete, location.pathname, location.search, navigate]);

    const handleStep = useCallback(
        async (direction: number) => {
            const currentStepIndex = stepsKeys.findIndex((value: string) => value === currentStep);
            const tmpStepIndex = currentStepIndex + direction;
            const newStepIndex =
                tmpStepIndex < 0 ? stepsKeys.length - 1 : tmpStepIndex === stepsKeys.length ? 0 : tmpStepIndex;
            setCurrentStep(stepsKeys[newStepIndex]);
        },
        [currentStep],
    );

    useEffect(() => {
        document.getElementById('scroll-flex')?.scroll(0, 0);
    }, [currentStep]);

    const CurrentForm = SigninSteps[currentStep];

    // Every step sits on the blurred picture of the product (wide screens only) and is laid out as PandaDoc's sign-up:
    // the logo above the card, the language below it (Vova 29.10.2026).
    const displayBackgroundImage = breakpoints.lg;

    return (
        <Flex
            justify="center"
            align="center"
            style={{ ...styles.flex, backgroundImage: displayBackgroundImage ? `url(${Bg})` : 'unset' }}
        >
            <Flex
                style={styles.mainWrapper}
                id="scroll-flex"
            >
                {/* margin: auto centres the column and still lets a tall step scroll from its top */}
                <Flex
                    vertical
                    align="center"
                    style={{ margin: breakpoints.lg ? 'auto' : 'auto 20px' }}
                >
                    <img
                        src={displayBackgroundImage ? LogoWhite : Logo}
                        // The size is known before the file arrives, or the card below jumps once it loads.
                        width={162}
                        height={displayBackgroundImage ? 34 : 48}
                        style={styles.topLogo}
                        alt="Tapfiliate"
                    />
                    <div style={styles.main}>
                        {isComplete ? <CompleteStep /> : <CurrentForm handleStep={handleStep} />}
                    </div>
                    {!isComplete && (
                        <div style={{ marginTop: 16 }}>
                            <LanguageDropdown textColor={displayBackgroundImage ? '#fff' : '#052d57'} />
                        </div>
                    )}
                </Flex>
            </Flex>
        </Flex>
    );
}
